import { Model, Relation } from '@nozbe/watermelondb'
import { date, field, immutableRelation, json, text, writer } from '@nozbe/watermelondb/decorators'
import { BelongsToAssociation } from '@nozbe/watermelondb/Model'
import {
  ChatMessageType,
  ChatSource,
  ComponentMessageContactVO,
  ComponentMessageLocationVO,
  ComponentMessageMediaVO,
  ComponentMessageStatusVO,
  ComponentMessageVO
} from '@repo/types'
import Chat from './Chat'

class Message extends Model {
  static table = 'messages'
  static associations: { chats: BelongsToAssociation } = {
    chats: { type: 'belongs_to', key: 'chat_id' }
  }

  @immutableRelation('chats', 'chat_id')
  chat: Relation<Chat>

  @text('connection_key') connectionKey: string
  @text('phone_number') phoneNumber: string
  @text('text') text: string
  @text('name') name: string
  @text('message_id') messageId: string
  @text('flow_id') flowId: string | null
  @text('status') status: string
  @text('source') source: ChatSource
  @text('type') type: ChatMessageType
  @text('user_id') userId: string
  @text('user_name') userName: string
  @text('header') header: string | null
  @text('header_media_url') headerMediaUrl: string | null
  @text('footer') footer: string | null
  @text('source_url') sourceUrl: string | null
  @text('source_type') sourceType: string

  @date('at') at: Date

  @field('media_id') mediaId: number | null

  @json('medias', json => json) medias: ComponentMessageMediaVO[] | null
  @json('status_details', json => json) statusDetails: ComponentMessageStatusVO[]
  @json('contacts', json => json) contacts: ComponentMessageContactVO[] | null

  @json('buttons', json => json) buttons: string[] | null
  @json('header_media', json => json) headerMedia: ComponentMessageMediaVO | null
  @json('location', json => json) location: ComponentMessageLocationVO | null
  @json('reply_message', json => json) replyMessage: ComponentMessageVO | null

  setMessageByComponentMessageVO(vo: ComponentMessageVO, chat: Chat) {
    this.connectionKey = vo.connectionKey
    this.phoneNumber = vo.phoneNumber
    this.text = vo.text ?? null
    this.name = vo.name
    this.messageId = vo.messageId
    this.flowId = vo.flowId ?? null
    this.userId = vo.userId
    this.userName = vo.userName
    this.mediaId = vo.mediaId ?? null
    this.at = vo.at
    this.status = vo.status
    this.source = vo.source
    this.type = vo.type
    this.statusDetails = vo.statusDetails ?? []
    this.medias = vo.medias ?? null
    this.contacts = vo.contacts ?? null
    this.buttons = vo.buttons ?? null
    this.header = vo.header ?? null
    this.headerMediaUrl = vo.headerMediaUrl ?? null
    this.footer = vo.footer ?? null
    this.location = vo.location ?? null
    this.replyMessage = vo.replyMessage ?? null
    this.sourceUrl = vo.sourceUrl ?? null
    this.sourceType = vo.sourceType
    this.chat.set(chat)
  }

  @writer
  async updateMessage(payload: ComponentMessageVO) {
    await this.update(message => this.updateMessageSet(message, payload))
  }

  prepareUpdateMessage(payload: ComponentMessageVO) {
    return this.prepareUpdate(message => this.updateMessageSet(message, payload))
  }

  private updateMessageSet(message: Message, payload: ComponentMessageVO) {
    message.connectionKey = payload.connectionKey
    message.phoneNumber = payload.phoneNumber
    message.text = payload.text ?? null
    message.name = payload.name
    message.messageId = payload.messageId
    message.flowId = payload.flowId ?? null
    message.status = payload.status
    message.source = payload.source
    message.type = payload.type
    message.userId = payload.userId
    message.userName = payload.userName
    message.at = payload.at
    message.mediaId = payload.mediaId ?? null
    message.medias = payload.medias ?? null
    message.statusDetails = payload.statusDetails ?? []
    message.header = payload.header ?? null
    message.headerMediaUrl = payload.headerMediaUrl ?? null
    message.footer = payload.footer ?? null
    message.buttons = payload.buttons ?? null
    message.contacts = payload.contacts ?? null
    message.location = payload.location ?? null
    message.replyMessage = payload.replyMessage ?? null
    message.sourceUrl = payload.sourceUrl ?? null
    message.sourceType = payload.sourceType
    return message
  }
}

export default Message
